import { io, Socket } from 'socket.io-client'
import config from './config'

class WebSocketService {
  private static instance: WebSocketService
  private socket: Socket | null = null
  private gameId: string | null = null
  private arenaId: string | null = null
  /** When set, socket joins user room for real-time invitations/rooms updates */
  private notificationUserId: string | null = null
  /** Room channels for live messenger updates */
  private roomIds: Set<string> = new Set()
  private callbacks: Map<string, Set<(data: any) => void>> = new Map()
  private reconnectAttempts = 0
  private maxReconnectAttempts = 15
  private reconnectInterval = 1000
  private pollingInterval: NodeJS.Timeout | null = null
  private isConnecting = false
  private connectionStatusCallbacks: ((connected: boolean) => void)[] = []
  private arenaListenersAttached = false

  // Singleton pattern
  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService()
    }
    return WebSocketService.instance
  }

  connect(gameId: string) {
    this.gameId = gameId
    this.reconnectAttempts = 0

    if (!this.socket || !this.socket.connected) {
      this.establishConnection()
    } else {
      // Already connected, join game immediately
      this.joinGameAfterConnection()
    }
    this.startPolling()
  }

  private joinGameAfterConnection() {
    const storedUser = localStorage.getItem('user')
    const user = storedUser ? JSON.parse(storedUser) : null
    const userId = user?._id || user?.id

    if (userId && this.gameId) {
      console.log(`🔌 Joining game room: chess:join-game for game: ${this.gameId}, user: ${userId}`)
      this.socket?.emit('chess:join-game', { gameId: this.gameId, userId })
    } else {
      console.warn('⚠️ Cannot join game - missing userId or gameId', { userId, gameId: this.gameId })
    }
  }

  // --- Arena (chess-arenas backend) ---
  connectForArena(arenaId: string) {
    this.arenaId = arenaId != null ? String(arenaId) : null
    this.reconnectAttempts = 0
    if (!this.socket || !this.socket.connected) {
      this.establishConnection()
    } else {
      console.log('🔌 Joining arena:', this.arenaId)
      this.socket.emit('joinArena', { arenaId: this.arenaId })
    }
  }

  leaveArena(arenaId: string) {
    if (this.socket?.connected) {
      this.socket.emit('leaveArena', { arenaId })
    }
    if (this.arenaId === arenaId) this.arenaId = null
  }

  emitTakeSeat(arenaId: string, color: 'white' | 'black', user: { id: string; name: string; rating?: number }) {
    this.socket?.emit('takeSeat', { arenaId: String(arenaId ?? ''), color, user })
  }

  emitLeaveSeat(arenaId: string, color: 'white' | 'black') {
    this.socket?.emit('leaveSeat', { arenaId, color })
  }

  emitJoinWaitlist(arenaId: string, user: { id: string; name: string; rating?: number }) {
    this.socket?.emit('joinWaitlist', { arenaId, user })
  }

  emitLeaveWaitlist(arenaId: string, oderId: string) {
    this.socket?.emit('leaveWaitlist', { arenaId, oderId })
  }

  emitArenaMove(arenaId: string, move: string, fen: string, whiteTime?: number, blackTime?: number) {
    this.socket?.emit('arenaMove', { arenaId, move, fen, whiteTime, blackTime })
  }

  emitResetArenaGame(arenaId: string) {
    this.socket?.emit('resetArenaGame', { arenaId })
  }

  emitArenaGameEnd(arenaId: string, winner: 'white' | 'black' | 'draw', reason: string) {
    this.socket?.emit('arenaGameEnd', { arenaId, winner, reason })
  }

  emitArenaProposeDraw(arenaId: string, proposerColor: 'white' | 'black') {
    this.socket?.emit('arenaProposeDraw', { arenaId, proposerColor })
  }

  emitArenaAcceptDraw(arenaId: string, accepterColor: 'white' | 'black') {
    this.socket?.emit('arenaAcceptDraw', { arenaId, accepterColor })
  }

  emitArenaRejectDraw(arenaId: string, rejecterColor: 'white' | 'black') {
    this.socket?.emit('arenaRejectDraw', { arenaId, rejecterColor })
  }

  emitAcceptSeatOffer(arenaId: string, color: 'white' | 'black', userId: string) {
    this.socket?.emit('acceptSeatOffer', { arenaId, color, userId })
  }

  emitRejectSeatOffer(arenaId: string, color: 'white' | 'black', userId?: string) {
    this.socket?.emit('rejectSeatOffer', { arenaId, color, userId })
  }

  // Connect to room for room-specific events
  connectToRoom(roomId: string) {
    if (!this.socket || !this.socket.connected) {
      this.establishConnection()
    }

    const storedUser = localStorage.getItem('user')
    const user = storedUser ? JSON.parse(storedUser) : null
    const userId = user?._id

    if (this.socket && userId) {
      const joinRoom = () => {
        if (this.socket && this.socket.connected) {
          console.log(`🔌 Joining room: ${roomId} for user: ${userId}`)
          // Join the room channel - backend expects 'join-room' event
          this.socket.emit('join-room', { roomId, userId })
          
          // Log room join confirmation
          console.log(`✅ Room join request sent for room: ${roomId}`)
        } else {
          console.warn(`⚠️ Cannot join room ${roomId} - socket not connected`)
        }
      }

      // Set up connect listener if not already connected
      if (!this.socket.connected) {
        this.socket.once('connect', () => {
          console.log('🔌 Socket connected, joining room:', roomId)
          joinRoom()
        })
      } else {
        // Already connected, join immediately
        joinRoom()
      }
    } else {
      console.warn(`⚠️ Cannot join room ${roomId} - missing socket or userId`)
    }
  }
  
  // Emit game creation event to notify other players
  emitGameCreated(roomId: string, game: any) {
    if (this.socket && this.socket.connected) {
      console.log(`📡 Emitting chess:game-created event for room: ${roomId}`)
      this.socket.emit('chess:game-created', { roomId, game })
    }
  }

  // Get connection status
  isConnected(): boolean {
    return this.socket ? this.socket.connected : false
  }

  // Add connection status callback
  onConnectionStatus(callback: (connected: boolean) => void) {
    this.connectionStatusCallbacks.push(callback)
  }

  // Remove connection status callback
  offConnectionStatus(callback: (connected: boolean) => void) {
    this.connectionStatusCallbacks = this.connectionStatusCallbacks.filter((cb) => cb !== callback)
  }

  private notifyConnectionStatus(connected: boolean) {
    this.connectionStatusCallbacks.forEach((callback) => callback(connected))
  }

  private establishConnection() {
    if (this.isConnecting) {
      return
    }

    this.isConnecting = true

    // Connect to the Socket.IO server
    const wsUrl = config.baseURL.replace('/api', '')

    try {
      this.socket = io(wsUrl, {
        transports: ['websocket'], // PERFORMANCE: Prefer websocket only (faster than polling)
        autoConnect: true,
        timeout: 10000, // PERFORMANCE: Reduced timeout for faster connection
        forceNew: false,
        withCredentials: false,
        reconnection: true,
        reconnectionDelay: 1000, // PERFORMANCE: Faster reconnection
        reconnectionDelayMax: 5000, // PERFORMANCE: Reduced max delay
        reconnectionAttempts: this.maxReconnectAttempts,
        upgrade: false, // PERFORMANCE: Don't upgrade from polling (we use websocket directly)
        rememberUpgrade: false, // PERFORMANCE: Not needed since we use websocket directly
        closeOnBeforeunload: false,
      })

      this.socket.on('connect', () => {
        console.log('✅ WebSocket connected, socket ID:', this.socket?.id)
        this.reconnectAttempts = 0
        this.isConnecting = false
        this.notifyConnectionStatus(true)
        if (this.notificationUserId) {
          this.socket?.emit('register-user', { userId: this.notificationUserId })
        }
        this.roomIds.forEach((rid) => {
          this.socket?.emit('register-room', { roomId: rid })
        })
        if (this.arenaId) {
          const aid = String(this.arenaId)
          console.log('🔌 Joining arena:', aid)
          this.socket?.emit('joinArena', { arenaId: aid })
        }
        if (this.gameId) {
          this.joinGameAfterConnection()
        }
      })

      this.socket.on('disconnect', (reason) => {
        this.notifyConnectionStatus(false)
        this.attemptReconnect()
      })

      this.socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error)
        this.isConnecting = false
        this.notifyConnectionStatus(false)
        this.attemptReconnect()
      })

      // Enhanced game event listeners
      this.socket.on('chess:move-made', (data) => {
        console.log('📨 Received chess:move-made event:', {
          gameId: data.gameId,
          move: data.move,
          userId: data.userId,
          hasGameState: !!data.gameState,
        })
        this.triggerCallback('move-made', data)
      })

      this.socket.on('chess:game-updated', (data) => {
        this.triggerCallback('game-state-update', {
          gameId: data.gameId || data._id,
          gameState: data.game || data,
        })
      })

      this.socket.on('chess:time-updated', (data) => {
        this.triggerCallback('timer-update', data)
      })

      this.socket.on('chess:game-started', (data) => {
        this.triggerCallback('game-started', data)
      })

      this.socket.on('chess:time-expired', (data) => {
        this.triggerCallback('time-expired', data)
      })

      // Listen for game creation events
      this.socket.on('chess:game-created', (data) => {
        console.log('📨 Received chess:game-created event:', data)
        this.triggerCallback('chess:game-created', data)
      })

      // Listen for room-specific game events
      this.socket.on('game-created', (data) => {
        console.log('📨 Received game-created event:', data)
        this.triggerCallback('chess:game-created', data)
      })

      // Specific handlers for draw and resign proposals
      this.socket.on('chess:draw-proposed', (data) => {
        console.log('📨 Received chess:draw-proposed event:', {
          gameId: data.gameId,
          hasGameState: !!data.gameState,
        })
        this.triggerCallback('draw-proposed', {
          gameId: data.gameId || data._id,
          gameState: data.game || data.gameState || data,
        })
      })

      this.socket.on('chess:resign-proposed', (data) => {
        console.log('📨 Received chess:resign-proposed event:', {
          gameId: data.gameId,
          hasGameState: !!data.gameState,
        })
        this.triggerCallback('resign-proposed', {
          gameId: data.gameId || data._id,
          gameState: data.game || data.gameState || data,
        })
      })

      this.socket.on('invitation:new', (data: any) => this.triggerCallback('invitation:new', data))
      this.socket.on('invitation:updated', (data: any) => this.triggerCallback('invitation:updated', data))
      this.socket.on('rooms:refresh', (data: any) => this.triggerCallback('rooms:refresh', data))
      this.socket.on('room:message:new', (data: any) => this.triggerCallback('room:message:new', data))

      this.attachArenaListeners()
    } catch (error) {
      console.error('Socket.IO: Failed to establish connection:', error)
      this.notifyConnectionStatus(false)
    }
  }

  /** Connect for real-time invitation and room updates (call when user is logged in). */
  connectForNotifications(userId: string) {
    const id = String(userId).trim()
    if (!id) return
    this.notificationUserId = id
    if (!this.socket || !this.socket.connected) {
      this.establishConnection()
    } else {
      this.socket.emit('register-user', { userId: id })
    }
  }

  /** Join a room channel for live messenger updates. */
  connectForRoom(roomId: string) {
    const id = String(roomId).trim()
    if (!id) return
    this.roomIds.add(id)
    if (!this.socket || !this.socket.connected) {
      this.establishConnection()
    } else {
      this.socket.emit('register-room', { roomId: id })
    }
  }

  leaveRoom(roomId: string) {
    const id = String(roomId).trim()
    if (!id) return
    this.roomIds.delete(id)
    this.socket?.emit('leave-room', { roomId: id })
  }

  private attachArenaListeners() {
    if (!this.socket || this.arenaListenersAttached) return
    this.arenaListenersAttached = true
    const arenaEvents = [
      'seatUpdate',
      'seatTakeError',
      'waitlistUpdate',
      'arenaGameUpdate',
      'arenaGameState',
      'arenaGameStarted',
      'arenaGameEnded',
      'arenaGameReset',
      'arenaDrawProposed',
      'arenaDrawRejected',
      'seatOffered',
    ] as const
    arenaEvents.forEach((ev) => {
      this.socket!.on(ev, (data: any) => this.triggerCallback(`arena:${ev}`, data))
    })
  }

  private attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

      setTimeout(() => {
        this.establishConnection()
      }, this.reconnectInterval * this.reconnectAttempts)
    }
  }

  private triggerCallback(event: string, data: any) {
    const cbs = this.callbacks.get(event)
    if (!cbs) return
    cbs.forEach((callback) => {
      try {
        callback(data)
      } catch (error) {
        console.error(`Error in callback for ${event}:`, error)
      }
    })
  }

  on(event: string, callback: (data: any) => void) {
    let cbs = this.callbacks.get(event)
    if (!cbs) {
      cbs = new Set()
      this.callbacks.set(event, cbs)
    }
    cbs.add(callback)
  }

  off(event: string, callback?: (data: any) => void) {
    if (!callback) {
      this.callbacks.delete(event)
      return
    }
    const cbs = this.callbacks.get(event)
    if (!cbs) return
    cbs.delete(callback)
    if (cbs.size === 0) {
      this.callbacks.delete(event)
    }
  }

  disconnect() {
    if (this.socket) {
      const storedUser = localStorage.getItem('user')
      const user = storedUser ? JSON.parse(storedUser) : null
      const userId = user?._id

      if (userId && this.gameId) {
        this.socket.emit('chess:leave-game', { gameId: this.gameId, userId })
      }
      if (this.arenaId) {
        this.socket.emit('leaveArena', { arenaId: this.arenaId })
      }
      this.socket.disconnect()
      this.socket = null
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
    this.callbacks.clear()
    this.connectionStatusCallbacks = []
    this.gameId = null
    this.arenaId = null
    this.notificationUserId = null
    this.arenaListenersAttached = false
    this.notifyConnectionStatus(false)
  }

  private startPolling() {
    // Only poll if WebSocket is disconnected (backup mechanism)
    this.pollingInterval = setInterval(async () => {
      // Skip polling if WebSocket is connected
      if (this.socket && this.socket.connected) {
        return
      }

      if (this.gameId) {
        try {
          // Import dynamically to avoid circular dependencies
          const chessGamesService = (await import('./chessGamesService')).default
          const game = await chessGamesService.getGame(this.gameId)

          // Check for new moves by comparing move count
          const stateKey = `game_${this.gameId}_state`
          const storedGameState = localStorage.getItem(stateKey)
          let lastKnownMovesCount = 0

          if (storedGameState) {
            const parsed = JSON.parse(storedGameState)
            lastKnownMovesCount = parsed.movesCount || 0
          }

          const currentMovesCount = game.moves ? game.moves.length : 0
          if (currentMovesCount > lastKnownMovesCount) {
            // Trigger callback with game state
            this.triggerCallback('move-made', {
              gameId: this.gameId,
              gameState: game,
            })
          }

          // Update stored game state
          localStorage.setItem(
            stateKey,
            JSON.stringify({
              movesCount: currentMovesCount,
              status: game.status,
              lastUpdate: Date.now(),
            })
          )
        } catch (error) {
          console.error('Error polling game updates:', error)
        }
      }
    }, 10000) // Poll every 10 seconds, only when WebSocket disconnected
  }
}

export const webSocketService = WebSocketService.getInstance()

